// One place that knows how to label, colour and describe every event type.

import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Bone,
  Brush,
  Cctv,
  Camera,
  Droplet,
  Droplets,
  Footprints,
  GraduationCap,
  HeartPulse,
  House,
  Moon,
  Pill,
  Scale,
  Compass,
  DoorOpen,
  StickyNote,
  Stethoscope,
  ToyBrick,
  Utensils,
  Zap,
} from 'lucide-react';
import { BEHAVIOURS } from '../content/behaviour.ts';
import { PooIcon } from '../ui/icons.ts';
import type { EventType, LogEvent } from '../domain/types.ts';
import { formatDuration, formatTime } from '../domain/time.ts';
import type { Tone } from '../ui/tone.ts';

export interface EventMeta {
  label: string;
  icon: LucideIcon;
  tone: Tone;
}

export const EVENT_META: Record<EventType, EventMeta> = {
  wee: { label: 'Wee', icon: Droplet, tone: 'warn' },
  poo: { label: 'Poo', icon: PooIcon, tone: 'accent' },
  meal: { label: 'Meal', icon: Utensils, tone: 'good' },
  water: { label: 'Water', icon: Droplets, tone: 'info' },
  treat: { label: 'Chew / treat', icon: Bone, tone: 'accent' },
  sleep: { label: 'Sleep', icon: Moon, tone: 'sleep' },
  walk: { label: 'Outing', icon: Footprints, tone: 'good' },
  play: { label: 'Play', icon: ToyBrick, tone: 'accent' },
  behaviour: { label: 'Behaviour', icon: Zap, tone: 'warn' },
  training: { label: 'Training', icon: GraduationCap, tone: 'info' },
  social: { label: 'Socialising', icon: Compass, tone: 'good' },
  weight: { label: 'Weight', icon: Scale, tone: 'neutral' },
  groom: { label: 'Grooming', icon: Brush, tone: 'info' },
  meds: { label: 'Medication', icon: Pill, tone: 'bad' },
  health: { label: 'Health note', icon: HeartPulse, tone: 'bad' },
  care: { label: 'Care done', icon: Stethoscope, tone: 'good' },
  alone: { label: 'Alone time', icon: House, tone: 'sleep' },
  note: { label: 'Note', icon: StickyNote, tone: 'neutral' },
  photo: { label: 'Photo', icon: Camera, tone: 'accent' },
  tracker: { label: 'Tracker', icon: Activity, tone: 'info' },
  camera: { label: 'Camera alert', icon: Cctv, tone: 'warn' },
  visit: { label: 'Visit', icon: DoorOpen, tone: 'good' },
};

const GROOM_LABELS: Record<string, string> = {
  brush: 'brush',
  ears: 'ears',
  nails: 'nails',
  teeth: 'teeth',
  bath: 'bath',
  eyes: 'eyes',
  feathers: 'feather trim',
  pro: 'groomer',
};

const SYMPTOMS: Record<string, string> = {
  vomit: 'Sick',
  diarrhoea: 'Diarrhoea',
  'not-eating': 'Off food',
  lethargic: 'Lethargic',
  itching: 'Itching',
  'ear-shaking': 'Head/ear shaking',
  limping: 'Limping',
  coughing: 'Coughing',
  eye: 'Eye problem',
  'ate-something': 'Ate something',
  other: 'Other',
};

export function symptomLabel(s: string) {
  return SYMPTOMS[s] ?? s;
}

/** "Sam", "Sam and Jo", "Sam, Jo and Kit". */
export function joinNames(names: string[]): string {
  return names.length <= 1 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Headline and detail for an event, e.g. "Wee · outside". */
export function describeEvent(e: LogEvent, lookup?: { skill?: (id: string) => string | undefined; social?: (id: string) => string | undefined }): { title: string; detail?: string } {
  const dur = e.end !== undefined ? formatDuration(e.end - e.at) : undefined;
  // The row already shows when it started; say when it finished too, so nobody has to add it up.
  const until = (word: string) => (e.end !== undefined ? `${word} ${formatTime(e.end)} · ${dur}` : undefined);
  switch (e.type) {
    case 'wee':
    case 'poo': {
      const inside = e.data.where === 'inside';
      const q = e.type === 'poo' && e.data.quality && e.data.quality !== 'firm' ? ` · ${e.data.quality}` : '';
      return { title: inside ? `${EVENT_META[e.type].label} indoors` : `${EVENT_META[e.type].label} outside`, detail: inside ? `Accident${q}` : q ? q.slice(3) : undefined };
    }
    case 'meal': {
      const bits = [e.data.grams ? `${e.data.grams} g` : '', e.data.appetite && e.data.appetite !== 'all' ? `ate ${e.data.appetite}` : e.data.appetite === 'all' ? 'ate it all' : ''];
      return { title: 'Meal', detail: bits.filter(Boolean).join(' · ') || undefined };
    }
    case 'sleep':
      return { title: e.end === undefined ? 'Asleep' : 'Nap', detail: e.end === undefined ? `${formatDuration(Math.max(0, Date.now() - e.at))} so far` : until('woke') };
    case 'walk':
      return {
        title: e.data.auto === 'pending' ? 'Outing (from phone)' : e.data.carried ? 'Carried outing' : 'Outing',
        detail: [e.end === undefined ? 'in progress' : until('back'), e.data.distanceKm ? `${(e.data.distanceKm / 1.609344).toFixed(2)} mi` : '', e.data.auto === 'pending' ? 'was Arlo there?' : ''].filter(Boolean).join(' · '),
      };
    case 'behaviour':
      return { title: BEHAVIOURS.find((b) => b.id === (e as LogEvent<'behaviour'>).data.kind)?.label ?? 'Behaviour' };
    case 'play':
      return { title: 'Play', detail: e.end === undefined ? 'in progress' : until('until') };
    case 'alone':
      return { title: 'Alone time', detail: e.end === undefined ? 'in progress' : `${until('until')}${e.data.settled === false ? ' · unsettled' : e.data.settled ? ' · settled' : ''}` };
    case 'training': {
      const names = e.data.skills.map((s) => lookup?.skill?.(s) ?? s);
      return { title: 'Training', detail: names.join(', ') || undefined };
    }
    case 'social': {
      const r = { happy: 'happy', unsure: 'unsure', worried: 'worried' }[e.data.reaction];
      return { title: lookup?.social?.(e.data.itemId) ?? 'New experience', detail: r };
    }
    case 'visit': {
      const who = joinNames(e.data.names);
      const r = e.data.reaction ? { happy: 'happy', unsure: 'unsure', worried: 'worried' }[e.data.reaction] : '';
      return { title: who ? `Visit: ${who}` : 'Visitors', detail: [e.end === undefined ? 'here now' : until('left'), r].filter(Boolean).join(' · ') || undefined };
    }
    case 'weight':
      return { title: `${e.data.kg.toFixed(2).replace(/0$/, '')} kg`, detail: 'Weigh-in' };
    case 'groom':
      return { title: 'Grooming', detail: e.data.tasks.map((t) => GROOM_LABELS[t] ?? t).join(', ') };
    case 'meds':
      return { title: e.data.name, detail: e.data.dose };
    case 'health':
      return { title: symptomLabel(e.data.symptom), detail: e.data.severity ? ['', 'mild', 'moderate', 'severe'][e.data.severity] : undefined };
    case 'care':
      return { title: e.data.title, detail: 'Done' };
    case 'camera':
      return { title: { bark: 'Barking detected', cry: 'Crying or whining', animal: 'Movement (animal)', motion: 'Motion', person: 'Person detected', sound: 'Sound detected' }[e.data.kind], detail: e.data.camera };
    case 'treat':
      return { title: e.data.what || 'Chew / treat' };
    case 'tracker':
      return { title: 'Tracker', detail: [e.data.activeMin ? `${e.data.activeMin} min active` : '', e.data.nightSleepMin ? `${Math.round(e.data.nightSleepMin / 60)} h night sleep` : ''].filter(Boolean).join(' · ') };
    default:
      return { title: EVENT_META[e.type].label };
  }
}
